import type { AttendanceVisit } from '@/types/attendance'
import type { AttendanceModuleConfig } from '@/types/space-wizard'
import { retentionError, retentionInput } from '@/components/features/spaces/retentionUtils'

/** What the SDK throws (axios-like): the status and the API's error body. */
type ApiFailure = { message?: string; response?: { data?: { message?: string; errors?: Array<{ param?: string; message?: string }> } } }

const asFailure = (err: unknown): ApiFailure => (err && typeof err === 'object' ? err as ApiFailure : {})

export const apiMessage = (err: unknown, fallback: string): string =>
  asFailure(err).response?.data?.message || asFailure(err).message || fallback

export const DEFAULT_AUTO_CHECKOUT = '23:59'
const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/

export const DEFAULT_KIOSK_RESET_SECONDS = 10

/** What a space is born with (F-78 §6.2): only the team marks, the day closes at 23:59, the kiosk asks for the name only. */
export const defaultAttendanceConfig = (): AttendanceModuleConfig => ({
  selfCheckIn: 'off',
  capacity: null,
  autoCheckoutTime: DEFAULT_AUTO_CHECKOUT,
  showWhoIsHere: 'staff',
  kiosk: { guestFields: { company: 'off', phone: 'off' }, notifyHost: true, resetSeconds: DEFAULT_KIOSK_RESET_SECONDS },
  dayPasses: false
})

/** The defaults under what the space stores, so a space saved before the kiosk fields existed shows them filled. */
export const mergeAttendanceConfig = (stored?: AttendanceModuleConfig | null): AttendanceModuleConfig => {
  const base = defaultAttendanceConfig()
  return {
    ...base,
    ...(stored ?? {}),
    kiosk: {
      ...base.kiosk,
      ...(stored?.kiosk ?? {}),
      guestFields: { ...base.kiosk?.guestFields, ...(stored?.kiosk?.guestFields ?? {}) }
    }
  }
}

export interface AttendanceConfigErrors {
  capacity?: string
  autoCheckoutTime?: string
  resetSeconds?: string
  retentionMonths?: string
}

const inRange = (value: unknown, min: number, max: number) => Number.isInteger(value) && (value as number) >= min && (value as number) <= max

/** Same bounds as the API (`capacity` 1 to 100000, `HH:mm`, retention 6 to 60, reset 5 to 60); the server stays the judge. */
export const validateAttendanceConfig = (
  config: AttendanceModuleConfig,
  t: (key: string) => string
): AttendanceConfigErrors | null => {
  const errors: AttendanceConfigErrors = {}
  const capacity = config.capacity
  if (capacity !== null && capacity !== undefined && !inRange(capacity, 1, 100000)) {
    errors.capacity = t('attendance:config.capacityInvalid')
  }
  if (config.autoCheckoutTime !== undefined && !TIME_OF_DAY.test(config.autoCheckoutTime)) {
    errors.autoCheckoutTime = t('attendance:config.autoCheckoutInvalid')
  }
  const reset = config.kiosk?.resetSeconds
  if (reset !== undefined && !inRange(reset, 5, 60)) {
    errors.resetSeconds = t('attendance:config.kiosk.resetSeconds.invalid')
  }
  const retention = retentionError(config.retentionMonths, t)
  if (retention) errors.retentionMonths = retention
  return Object.keys(errors).length ? errors : null
}

/** The slice that goes to the API: only what this screen owns, the rest of the stored object is merged by the caller. */
export const toAttendanceInput = (config: AttendanceModuleConfig): AttendanceModuleConfig => {
  const merged = mergeAttendanceConfig(config)
  return {
    selfCheckIn: config.selfCheckIn ?? 'off',
    capacity: config.capacity ?? null,
    autoCheckoutTime: config.autoCheckoutTime || DEFAULT_AUTO_CHECKOUT,
    showWhoIsHere: 'staff',
    kiosk: merged.kiosk,
    dayPasses: config.dayPasses === true,
    ...retentionInput(config.retentionMonths)
  }
}

/** `{ param: 'moduleConfig.attendance.capacity', message }` into per-field messages. */
export const attendanceFieldErrors = (err: unknown): AttendanceConfigErrors => {
  const errors = asFailure(err).response?.data?.errors
  const list: Array<{ param?: string; message?: string }> = Array.isArray(errors) ? errors : []
  const find = (name: string) => list.find((e) => e.param?.endsWith(`attendance.${name}`))?.message
  return {
    capacity: find('capacity'),
    autoCheckoutTime: find('autoCheckoutTime'),
    resetSeconds: find('kiosk.resetSeconds'),
    retentionMonths: find('retentionMonths')
  }
}

/* ------------------------------------------------------------------ dates -- */

const safeZone = (timeZone?: string): string | undefined => {
  if (!timeZone) return undefined
  try {
    new Intl.DateTimeFormat('en', { timeZone })
    return timeZone
  } catch {
    return undefined
  }
}

export const formatTime = (iso: string | null | undefined, locale: string, timeZone?: string): string =>
  iso ? new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone: safeZone(timeZone) }).format(new Date(iso)) : ''

export const formatDay = (iso: string | null | undefined, locale: string, timeZone?: string): string =>
  iso ? new Intl.DateTimeFormat(locale, { weekday: 'short', day: '2-digit', month: 'short', timeZone: safeZone(timeZone) }).format(new Date(iso)) : ''

/** `yyyy-mm-dd` of the instant on the community's calendar. */
export const localDateKey = (iso: string | Date, timeZone?: string): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: safeZone(timeZone) }).format(typeof iso === 'string' ? new Date(iso) : iso)

/** `HH:mm` of the instant on the community's clock. */
export const localTimeKey = (iso: string, timeZone?: string): string =>
  new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: safeZone(timeZone) }).format(new Date(iso))

const zoneOffsetMs = (ts: number, timeZone?: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: safeZone(timeZone), hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
  }).formatToParts(new Date(ts))
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value)
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second')) - ts
}

/** A wall-clock day and time in the community's zone, as the UTC instant the API wants. */
export const zonedToIso = (dateKey: string, time: string, timeZone?: string): string => {
  const [y, m, d] = dateKey.split('-').map(Number)
  const [h, mi] = time.split(':').map(Number)
  const guess = Date.UTC(y, m - 1, d, h, mi)
  let ts = guess - zoneOffsetMs(guess, timeZone)
  ts = guess - zoneOffsetMs(ts, timeZone)
  return new Date(ts).toISOString()
}

/** "2h 05min", "45min". */
export const formatMinutes = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}min` : `${m}min`
}

/** `anonymized`: what a guest whose data the retention period cleared (F-203a) is called; defaults to `fallback`. */
export const visitName = (visit: AttendanceVisit, fallback: string, anonymized: string = fallback): string => {
  if (visit.kind !== 'guest') return visit.member?.name || fallback
  return visit.guest?.anonymized ? anonymized : visit.guest?.name || fallback
}

export const initialsOf = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('')
